import { resolveColour, type ColourRoleName } from "odyssey-shared";
import { LOW_HEALTH_STEADY_ROLE, healthLow, lowHealthLit } from "../../../ui/kit/health.ts";
import { HEALTH_BAR, barFillOf, barLayout } from "./bar.ts";

/**
 * A creature's health bar (R-0094, ADR-082's note of 2026-10-02; the design's `NpcNameplate`): the plate bar's 32 by 4
 * with its one-pixel frame edge and, unlike a player's, no highlight row. A hostile creature's fills in the health
 * colour inside a one-pixel `danger-dark` ring, the design's "attitude colour + bar shape"; any other attitude's fills
 * in the positive green with no ring. Every frame is cut from one strip of 34 by 6 cells drawn once, a calm bar leaving
 * its outer ring clear, so a change of health or tone changes the frame and nothing is drawn again. Below 10 percent
 * the fill flashes, or under reduced motion stays lit in the steady colour, by the kit's health rule.
 */

/**
 * The cell each frame fills in logical pixels: the 32 by 4 bar and its one-pixel ring; the inside its fill covers.
 */
export const CREATURE_BAR = Object.freeze({ width: HEALTH_BAR.width + 2, height: HEALTH_BAR.height + 2, inside: HEALTH_BAR.inside });

/**
 * The texture key the strip is drawn into.
 */
export const CREATURE_BAR_TEXTURE = "creature-health-bar";

/**
 * A bar's look: `hostile`, ringed and in the health colour, or `calm`, unringed and green.
 */
export type BarToneName = "hostile" | "calm";

/**
 * One run of frames: its tone, whether its fill is the steady colour, and its frame names, made once.
 */
interface Run {
  readonly tone: BarToneName;
  readonly steady: boolean;
  readonly names: readonly string[];
}

/**
 * The runs in the strip's order from the top.
 */
const RUNS: readonly Run[] = Object.freeze(
  (["hostile", "calm"] as const).flatMap((tone) =>
    [false, true].map((steady) => Object.freeze({ tone, steady, names: Object.freeze(Array.from({ length: CREATURE_BAR.inside + 1 }, (_, fill) => `${CREATURE_BAR_TEXTURE}/${tone}${steady ? "/steady" : ""}/${fill}`)) })),
  ),
);

/**
 * How many frames the strip holds.
 */
export const CREATURE_BAR_FRAMES = RUNS.length * (CREATURE_BAR.inside + 1);

/**
 * The run of a tone in the health colour or the steady colour.
 *
 * @param tone The tone.
 * @param steady Whether the steady colour's run is wanted.
 * @returns The run's frame names.
 */
function namesOf(tone: BarToneName, steady: boolean): readonly string[] {
  return (RUNS[(tone === "hostile" ? 0 : 2) + (steady ? 1 : 0)] as Run).names;
}

/**
 * The frame a creature's bar shows for a Health percentage at a moment of the scene's clock, as the plate bar's
 * `barFrameOf` chooses: the fill's frame, flashing to the empty track below 10 percent, or under reduced motion the
 * fill in the steady colour. The names are made once, so nothing is allocated.
 *
 * @param tone The bar's tone.
 * @param health The percentage.
 * @param timeMs The scene's clock in ms.
 * @param reducedMotion Whether the viewer asks for reduced motion.
 * @returns The frame's name.
 */
export function creatureBarFrameOf(tone: BarToneName, health: number, timeMs: number, reducedMotion: boolean): string {
  const fill = barFillOf(health);
  if (!healthLow(health, 100)) {
    return namesOf(tone, false)[fill] as string;
  }
  if (reducedMotion) {
    return namesOf(tone, true)[fill] as string;
  }
  return namesOf(tone, false)[lowHealthLit(timeMs) ? fill : 0] as string;
}

/**
 * Every frame's name and its cell in the strip, in the strip's order, for cutting the frames from the texture.
 *
 * @param each Called with each frame's name and the index of its cell from the top.
 */
export function forEachCreatureBarFrame(each: (name: string, index: number) => void): void {
  RUNS.forEach((run, at) => run.names.forEach((name, fill) => each(name, at * (CREATURE_BAR.inside + 1) + fill)));
}

/**
 * A colour role as red, green, blue and full alpha.
 *
 * @param role The role.
 * @returns The four channels.
 */
function rgba(role: ColourRoleName): readonly [number, number, number, number] {
  const hex = resolveColour(role);
  return [Number.parseInt(hex.slice(1, 3), 16), Number.parseInt(hex.slice(3, 5), 16), Number.parseInt(hex.slice(5, 7), 16), 255];
}

const CLEAR = [0, 0, 0, 0] as const;

/**
 * Paints one run into the strip: one cell per fill from 0 to the inside, each a ring (danger-dark for a hostile bar,
 * clear for a calm one) around the frame edge around a two-row inside, the fill in its colour on both rows and the
 * rest the bar track.
 *
 * @param pixels The strip's pixels.
 * @param first The index of the run's first cell in the strip.
 * @param run The run.
 */
function paintRun(pixels: Uint8ClampedArray, first: number, run: Run): void {
  const { width, height, inside } = CREATURE_BAR;
  const ring = run.tone === "hostile" ? rgba("danger-dark") : CLEAR;
  const edge = rgba("frame-edge");
  const track = rgba("bar-track");
  const fillColour = rgba(run.steady ? LOW_HEALTH_STEADY_ROLE : run.tone === "hostile" ? "health" : "positive");
  for (let fill = 0; fill <= inside; fill += 1) {
    for (let row = 0; row < height; row += 1) {
      for (let x = 0; x < width; x += 1) {
        const outer = row === 0 || row === height - 1 || x === 0 || x === width - 1;
        const border = row === 1 || row === height - 2 || x === 1 || x === width - 2;
        const colour = outer ? ring : border ? edge : x - 2 < fill ? fillColour : track;
        const at = (((first + fill) * height + row) * width + x) * 4;
        pixels[at] = colour[0];
        pixels[at + 1] = colour[1];
        pixels[at + 2] = colour[2];
        pixels[at + 3] = colour[3];
      }
    }
  }
}

/**
 * The strip every creature bar's frames are cut from: the hostile run, its steady run, the calm run and its steady
 * run, stacked from the top.
 *
 * @returns The strip's size and its RGBA pixels.
 */
export function creatureHealthBarStrip(): { readonly width: number; readonly height: number; readonly pixels: Uint8ClampedArray } {
  const { width, height, inside } = CREATURE_BAR;
  const pixels = new Uint8ClampedArray(width * height * CREATURE_BAR_FRAMES * 4);
  RUNS.forEach((run, at) => paintRun(pixels, at * (inside + 1), run));
  return { width, height: height * CREATURE_BAR_FRAMES, pixels };
}

/**
 * Where a creature's bar cell stands, in logical pixels of the UI layer: a pixel up and left of the plate bar's place
 * (`barLayout`), so the 32 by 4 bar inside the ring lines up with a player's.
 *
 * @param anchorX The head's x in whole device pixels.
 * @param anchorY The head's y in whole device pixels.
 * @param scale The UI scale.
 * @param out Where the cell's top left is written.
 * @returns `out`.
 */
export function creatureBarLayout(anchorX: number, anchorY: number, scale: number, out: { x: number; y: number }): { x: number; y: number } {
  barLayout(anchorX, anchorY, scale, out);
  out.x -= 1;
  out.y -= 1;
  return out;
}
